/**
 * 파일명 : evl0107m.tsx
 * 설명 : 프로젝트평가(PM) 화면
 * 수정내역
 * **********************************************
 * Version    수정일자      수정자    수정내용
 * **********************************************
 * 1.0       2026-01-14     조동현    최초작성
 */

/************************************************
 * Import 영역
 ************************************************/

import React, { useMemo, useState } from "react";
import {
  Form,
  Input,
  Table,
  Select,
  Typography,
  Card,
  Button,
  Tooltip,
  Alert,
  message,
  Modal,
  Spin,
} from "antd";
import * as commonProxy from "libs/CommonProxy";
import * as common from "libs/common";
import { useRef } from "react";
import { useAppSelector, useAppDispatch } from "../../../redux/hooks";
import { EvaluationItem } from "../../../redux/models/evl/evl0107m_model";
import { Evl0107mAction } from "../../../redux/slice/evl/evl0107m_slice";

const { Title } = Typography;
const { TextArea } = Input;

export default function Evl0107m(props: any) {
  // Ant Design Form 인스턴스
  const [form] = Form.useForm();

  const [loading, setLoading] = useState<boolean>(true);
  const [isWithinDeadline, setIsWithinDeadline] = useState<boolean>(false);

  // Redux에서 evl0101m에서 전달한 props 받기
  const activeProps = useAppSelector((state) => state.Frame.activeProps);

  const scrollRef = useRef<HTMLDivElement | null>(null);
  const evaluationTableRef = useRef<HTMLDivElement | null>(null);
  const commentsRef = useRef<HTMLDivElement | null>(null);

  // Modal 관리용 ref
  const confirmModalRefs = useRef<any[]>([]);

  const removeConfirmModalRef = (targetModal: any) => {
    if (!targetModal) return;
    confirmModalRefs.current = confirmModalRefs.current.filter(
      (modalRef) => modalRef !== targetModal,
    );
  };

  const closeOpenConfirmModals = () => {
    confirmModalRefs.current.forEach((modalRef) => {
      try {
        modalRef?.destroy?.();
      } catch (error) {
        // ignore
      }
    });
    confirmModalRefs.current = [];
  };

  const openConfirmModal = (config: any) => {
    let createdModal: any;
    createdModal = Modal.confirm({
      ...config,
      onOk: (...args: any[]) => {
        const result = config?.onOk?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
      onCancel: (...args: any[]) => {
        const result = config?.onCancel?.(...args);
        removeConfirmModalRef(createdModal);
        return result;
      },
    });

    confirmModalRefs.current.push(createdModal);
    return createdModal;
  };

  const scrollAndError = (
    ref: React.RefObject<HTMLElement>,
    errorMsg: string,
  ) => {
    message.error(errorMsg);
    setTimeout(() => {
      if (ref.current) {
        ref.current.scrollIntoView({ behavior: "smooth", block: "center" });
        ref.current.focus?.();
      }
    }, 300);
  };

  const scrollToRowSelector = (selector: string) => {
    setTimeout(() => {
      const element = document.querySelector(selector);
      if (element) {
        element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 300);
  };

  // Redux 상태 및 디스패치
  const dispatch = useAppDispatch();
  const {
    pmScores,
    projectInfo,
    evaluationData,
    alertInfo,
    isSubmitted,
    pmSubYn,
  } = useAppSelector((state) => state.Evl0107m);

  const isPreview = props?.viewMode === "preview";
  const isReadonly =
    props.viewMode === "readonly" || props.pjPmEmpNo !== common.getEmpNo();
  const isViewMode = !!props?.viewMode;
  const isReadOnlyMode = isViewMode || isSubmitted || pmSubYn === "Y";


  /**
   * 그룹 크기 및 첫 번째 키 계산
   */
  const { groupSizes, firstKeys } = useMemo(() => {
    const groups: Record<string, number> = {};
    const firsts: Record<string, string> = {};
    evaluationData.forEach((item) => {
      if (item.dtlTypCd) {
        groups[item.dtlTypCd] = (groups[item.dtlTypCd] || 0) + 1;
        if (!firsts[item.dtlTypCd]) {
          firsts[item.dtlTypCd] = item.key;
        }
      }
    });
    return { groupSizes: groups, firstKeys: firsts };
  }, [evaluationData]);

  /**
   * 화면 초기화 - 프로젝트 정보 조회
   * 컴포넌트 마운트 시 Redux에서 받은 props를 이용해 프로젝트 정보를 조회
   */
  React.useEffect(() => {
    if (props?.viewMode === "preview") {
      setLoading(true);
      loadEvaluationItems({}, true);
      form.resetFields(); // isPreview 모드에서는 폼 초기화
      return;
    } else {
      if (activeProps && activeProps.evlId && activeProps.empNo) {
        loadProjectInfo();
      } else if (
        activeProps === null ||
        activeProps === undefined ||
        !activeProps.evlId
      ) {
        setLoading(true);

        openConfirmModal({
          title: "인사평가 메인페이지에서 접근하십시오.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } }, // 취소 버튼 숨김
          maskClosable: false, // 바깥 클릭 방지
          keyboard: false, // ESC 방지
          onOk() {
            // 인사평가 메인 페이지로 이동
            common.openPage({ menuId: "MEVL2025002" });
          },
          onCancel() {
            // 인사평가 메인 페이지로 이동
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      }
    }
  }, [activeProps, props?.viewMode]);

  /**
   * 브라우저 뒤로가기 시 Modal 닫기
   */
  React.useEffect(() => {
    const handlePopState = () => {
      closeOpenConfirmModals();
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
      closeOpenConfirmModals();
    };
  }, []);

  /**
   * 프로젝트 정보 조회 함수
   * Redux에서 받은 props의 evlId와 empNo를 이용해 프로젝트 정보를 조회
   */
  const loadProjectInfo = () => {
    setLoading(true);
    // activeProps에서 받은 pjNm을 바로 설정
    dispatch(
      Evl0107mAction.setProjectInfo({
        ...projectInfo,
        pjNm: activeProps?.pjNm || "",
      }),
    );

    const requestData = {
      pjMapId: activeProps?.evlId || "",
      evlCd: "03", // 03 : 프로젝트평가PM
      empNo: activeProps?.empNo || "",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLR0014", // 프로젝트 정보 조회 트랜코드
      success: (responseData: any) => {
        let pjNm = activeProps?.pjNm || "";
        let userNm = "";
        let deptNm = "";

        // pmInfo 존재 여부 확인 후 접근
        if (responseData && responseData.pmInfo) {
          userNm = responseData.pmInfo.userNm || "";
          deptNm = responseData.pmInfo.deptNm || "";
        } else {
          message.error("pmInfo가 없습니다:", responseData);
        }

        // State에 프로젝트 정보 저장
        dispatch(
          Evl0107mAction.setProjectInfo({
            pjNm,
            userNm,
            deptNm,
          }),
        );

        // TREVLR0014 트랜잭션 완료 후 TREVLR0017 트랜잭션 호출
        loadEvaluationItems(requestData);
      },
      error: (err: any) => {
        message.error("프로젝트 정보 조회 실패:", err);
        setLoading(false);
      },
    });
  };

  /**
   * 평가 데이터 조회 함수
   * TREVLR0017 트랜잭션 완료 후 호출
   */
  const loadEvaluationData = (
    requestData: any,
    evaluationDataParam: EvaluationItem[],
    isInitial: boolean = false,
  ) => {
    const payload = {
      pjMapId: activeProps?.evlId || "",
      evlCd: "03",
      ...requestData,
    };
    commonProxy.callAsyncSvc(payload, {
      txCd: "TREVLR0015", // 평가 데이터 조회 트랜코드
      success: (responseData: any) => {
        if (isInitial) {
          message.success("프로젝트 평가내용 조회완료");
        }

        // responseData.PjtEvlPm에서 pmEvlRmk를 가져와서 기타 평가 의견에 설정
        const pmEvlRmk = responseData?.PjtEvlPm?.[0]?.pmEvlRmk;
        if (pmEvlRmk != null) {
          form.setFieldsValue({ comments: pmEvlRmk });
        }

        // responseData.PjtEvlPm 배열의 각 항목에서 pmEvlScr을 hrEvlItmId를 key로 매핑
        if (
          responseData &&
          responseData.PjtEvlPm &&
          Array.isArray(responseData.PjtEvlPm)
        ) {
          const newPmScores: Record<string, string> = {};

          responseData.PjtEvlPm.forEach((evalItem: any) => {
            if (evalItem.pmEvlScr && evalItem.hrEvlItmId) {
              newPmScores[evalItem.hrEvlItmId] = String(evalItem.pmEvlScr);
            }
          });

          dispatch(Evl0107mAction.setPmScores(newPmScores));

          // pmSubYn 설정 및 제출 상태 확인
          const pmSubYnValue = responseData.PjtEvlPm[0]?.pmSubYn || "";
          dispatch(Evl0107mAction.setPmSubYn(pmSubYnValue));
          if (pmSubYnValue === "Y") {
            dispatch(Evl0107mAction.setIsSubmitted(true));
          } else {
            dispatch(Evl0107mAction.setIsSubmitted(false));
          }
        }

        // 마감일 이내 여부 설정 (서버에서 계산한 값 사용)
        const isWithinDeadlineValue = responseData.isWithinDeadline === true || responseData.isWithinDeadline === "Y";
        setIsWithinDeadline(isWithinDeadlineValue);

        setLoading(false);
      },
      error: (err: any) => {
        message.error("평가 데이터 조회 실패:", err);
        setLoading(false);
      },
    });
  };

  /**
   * 평가 항목 조회 함수
   * TREVLR0014 트랜잭션 완료 후 호출
   */
  const loadEvaluationItems = (
    requestData: any,
    previewOnly: boolean = false,
  ) => {
    const payload = { evlCd: "03", ...requestData };
    commonProxy.callAsyncSvc(payload, {
      txCd: "TREVLR0017", // 평가 항목 조회 트랜코드
      success: (responseData: any) => {
        // 기타가감 항목(dtlTypCd: '11')을 마지막으로 정렬
        const otherItems = responseData.evlItem.filter(
          (item: any) => item.dtlTypCd === "11",
        );
        const mainItems = responseData.evlItem.filter(
          (item: any) => item.dtlTypCd !== "11",
        );
        const sortedItems = [...mainItems, ...otherItems];

        // 그룹별로 묶고 소계 추가
        const groups: Record<string, any[]> = {};
        sortedItems.forEach((item) => {
          if (!groups[item.dtlTypCd]) groups[item.dtlTypCd] = [];
          groups[item.dtlTypCd].push(item);
        });

        // 그룹 순서 유지
        const groupOrder = Array.from(
          new Set(sortedItems.map((item) => item.dtlTypCd)),
        );
        const mainGroupOrder = groupOrder.filter((dtl) => dtl !== "11");
        const otherGroupOrder = groupOrder.filter((dtl) => dtl === "11");

        const data: EvaluationItem[] = [];

        // 메인 그룹들
        mainGroupOrder.forEach((dtlTypCd) => {
          data.push(
            ...groups[dtlTypCd].map((item: any, index: number) => ({
              key: item.hrEvlItmId,
              category: item.itmNm,
              no: item.itmSeq,
              item: `${index + 1}. ${item.itmDesc}`,
              score: item.baseAsgn,
              pm: "",
              dtlTypCd: item.dtlTypCd,
            })),
          );

          // 소계 추가
          const subtotalScore = groups[dtlTypCd].reduce(
            (sum, item) => sum + item.baseAsgn,
            0,
          );
          data.push({
            key: `subtotal-${dtlTypCd}`,
            category: groups[dtlTypCd][0].itmNm,
            no: "",
            item: "소계",
            score: subtotalScore,
            pm: "",
            dtlTypCd: dtlTypCd,
          });
        });

        // 계 추가
        const totalScore = mainGroupOrder.reduce(
          (sum, dtlTypCd) =>
            sum + groups[dtlTypCd].reduce((s, item) => s + item.baseAsgn, 0),
          0,
        );
        data.push({
          key: "total",
          category: "",
          no: "",
          item: "계",
          score: totalScore,
          pm: "",
          dtlTypCd: "",
        });

        // 기타가감 그룹
        otherGroupOrder.forEach((dtlTypCd) => {
          data.push(
            ...groups[dtlTypCd].map((item: any, index: number) => ({
              key: item.hrEvlItmId,
              category: item.itmNm,
              no: item.itmSeq,
              item: `${index + 1}. ${item.itmDesc}`,
              score: item.baseAsgn,
              pm: "",
              dtlTypCd: item.dtlTypCd,
            })),
          );

          // 기타가감에는 소계 추가하지 않음
        });

        // 총계 추가
        const grandTotalScore =
          totalScore +
          otherGroupOrder.reduce(
            (sum, dtlTypCd) =>
              sum + groups[dtlTypCd].reduce((s, item) => s + item.baseAsgn, 0),
            0,
          );
        data.push({
          key: "grand-total",
          category: "",
          no: "",
          item: "총계",
          score: grandTotalScore,
          pm: "",
          dtlTypCd: "",
        });

        dispatch(Evl0107mAction.setEvaluationData(data));
        if (previewOnly) {
          setLoading(false);
          return;
        }
        loadEvaluationData(requestData, data, true);
      },
      error: (err: any) => {
        message.error("평가 항목 조회 실패:", err);
        setLoading(false);
      },
    });
  };

  /**
   * 저장 버튼 클릭 핸들러
   * 현재 입력된 평가 데이터를 저장하는 기능
   */
  const handleSave = () => {
    openConfirmModal({
      title: "저장하시겠습니까?",
      okText: "저장",
      cancelText: "취소",
      onOk: () => {
        const comments = form.getFieldValue("comments") || "";

        const evlList = Object.keys(pmScores).map((hrEvlItmId) => ({
          hrEvlItmId,
          pmEvlScr: pmScores[hrEvlItmId],
        }));

        const requestData = {
          pjMapId: activeProps.evlId,
          evlCd: "03", // 03 : 프로젝트평가PM
          pmEvlRmk: comments,
          pmEvlTotScr: calculateGrandTotal(),
          evlList,
        };

        commonProxy.callAsyncSvc(requestData, {
          txCd: "TREVLS0016",
          success: (responseData: any) => {
            const resultCode = responseData?.data?.resultCode || responseData?.resultCode;
            const resultMsg = responseData?.data?.resultMsg || responseData?.resultMsg;

            if (resultCode === "400") {
              openConfirmModal({
                title: "저장 실패",
                content: resultMsg || "저장에 실패했습니다.",
                okText: "확인",
                cancelButtonProps: { style: { display: "none" } },
                onOk: () => {
                  common.openPage({ menuId: "MEVL2025002" });
                },
              });
            } else {
              message.success("저장되었습니다.");
              // 저장 후 평가 데이터 재조회
              const reloadData = {
                pjMapId: activeProps.evlId,
                empNo: activeProps?.empNo || "",
              };
              loadEvaluationData(reloadData, evaluationData, false);
              window.scrollTo(0, 0);
            }
          },
          error: (err: any) => {
            message.error("저장에 실패했습니다.");
            window.scrollTo(0, 0);
          },
        });
      },
    });
  };

  /**
   * comments 입력 변경 핸들러
   * 2000자 초과 시 Modal.confirm으로 경고하고 2000자로 제한
   */
  const handleCommentsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    if (value.length > 500) {
      form.setFieldsValue({ comments: value.slice(0, 500) });
      openConfirmModal({
        title: "※ 500자를 초과하였습니다.",
        okText: "확인",
      });
    }
  };

  /**
   * 제출 버튼 클릭 핸들러
   * 평가 데이터를 최종 제출하는 기능
   * 평가하지 않은 항목이 있는지 체크 후 제출
   */
  const handleSubmit = () => {
    // 실제 평가 항목만 필터링 (소계, 계, 총계 제외, 기타가감 제외)
    const evaluationItems = evaluationData.filter(
      (item) =>
        item.no !== "" &&
        item.item !== "계" &&
        item.item !== "총계" &&
        item.dtlTypCd !== "11", // 기타가감은 필수검증 제외
    );

    // 평가하지 않은 항목 찾기
    const unevaluatedItems = evaluationItems.filter((item) => {
      const grade = item.key in pmScores ? pmScores[item.key] : item.pm;
      return !grade || grade === "";
    });

    // 평가하지 않은 항목이 있으면 경고
    if (unevaluatedItems.length > 0) {
      const countsByCategory = unevaluatedItems.reduce(
        (acc: Record<string, number>, item) => {
          const key = item.category || "미분류";
          acc[key] = (acc[key] || 0) + 1;
          return acc;
        },
        {},
      );
      const categoryOrder = evaluationItems.reduce((acc: string[], item) => {
        const key = item.category || "미분류";
        if (!acc.includes(key)) acc.push(key);
        return acc;
      }, []);
      const firstCategory = categoryOrder.find(
        (category) => (countsByCategory[category] || 0) > 0,
      );
      const fallbackCategory = Object.keys(countsByCategory)[0];
      const category = firstCategory || fallbackCategory || "미분류";
      const count = countsByCategory[category] || 0;
      message.error(`${category} - ${count}개의 항목이 평가되지 않았습니다.`);
      const firstMissing = unevaluatedItems[0];
      if (firstMissing) {
        scrollToRowSelector(`[data-eval-key="${firstMissing.key}"]`);
      } else {
        scrollToRowSelector("[data-eval-table='true']");
      }
      return;
    }

    const comments = (form.getFieldValue("comments") || "").trim();
    if (!comments) {
      Modal.warning({
        title: "기타 평가 의견 미입력",
        content: "기타 평가 의견을 입력해주세요.",
      });
      return;
    }

    // 기타가감 항목 선택 여부 확인
    const otherItem = evaluationData.find((item) => item.dtlTypCd === "11");
    const otherItemSelected =
      otherItem && otherItem.key in pmScores && pmScores[otherItem.key];

    // 기타가감 항목을 선택하지 않았을 경우
    if (!otherItemSelected) {
      openConfirmModal({
        title: "기타가감 항목 미선택 확인",
        content:
          "기타가감 항목을 선택하지 않았습니다. 그대로 제출하시겠습니까?",
        okText: "제출",
        cancelText: "취소",
        onOk: () => {
          submitEvaluation();
        },
      });
      return;
    }

    submitEvaluation();
  };

  /**
   * 평가 제출 실행 함수
   * handleSubmit에서 모든 검증을 완료한 후 호출
   */
  const submitEvaluation = () => {
    openConfirmModal({
      title: "제출하시겠습니까?",
      content: "제출 이후에는 수정이 불가합니다.",
      okText: "제출",
      cancelText: "취소",
      onOk: () => {
        const comments = form.getFieldValue("comments") || "";

        const evlList = Object.keys(pmScores).map((hrEvlItmId) => ({
          hrEvlItmId,
          pmEvlScr: pmScores[hrEvlItmId],
        }));

        const requestData = {
          pjMapId: activeProps.evlId,
          evlCd: "03", // 03 : 프로젝트평가PM
          pmEvlRmk: comments,
          pmEvlTotScr: calculateGrandTotal(),
          evlList,
        };

        commonProxy.callAsyncSvc(requestData, {
          txCd: "TREVLS0017",
          success: (responseData: any) => {
            const resultCode = responseData?.data?.resultCode || responseData?.resultCode;
            const resultMsg = responseData?.data?.resultMsg || responseData?.resultMsg;

            if (resultCode === "400") {
              openConfirmModal({
                title: "제출 실패",
                content: resultMsg || "제출에 실패했습니다.",
                okText: "확인",
                cancelButtonProps: { style: { display: "none" } },
                onOk: () => {
                  common.openPage({ menuId: "MEVL2025002" });
                },
              });
            } else {
              message.success("제출되었습니다.");
              setTimeout(() => {
                common.openPage({ menuId: "MEVL2025002" });
              }, 800);
            }
          },
          error: (err: any) => {
            message.error("제출에 실패했습니다.");
            window.scrollTo(0, 0);
          },
        });
      },
    });
  };

  /**
   * 제출취소 버튼 클릭 핸들러
   */
  const handleCancelSubmit = () => {
    openConfirmModal({
      title: "제출을 취소하시겠습니까?",
      content: "제출취소 후 기한 내에 다시 제출할 수 있습니다.",
      okText: "확인",
      cancelText: "취소",
      onOk: () => {
        performCancelSubmit();
      },
    });
  };

  /**
   * 실제 제출취소 로직 실행 (submitYn='N'으로 제출)
   */
  const performCancelSubmit = () => {
    const requestData = {
      pjMapId: activeProps.evlId,
      evlCd: "03",
      submitYn: "N",
    };

    commonProxy.callAsyncSvc(requestData, {
      txCd: "TREVLS0017",
      success: (responseData: any) => {
        const resultCode = responseData?.data?.resultCode || responseData?.resultCode;
        const resultMsg = responseData?.data?.resultMsg || responseData?.resultMsg;

        if (resultCode === "400") {
          openConfirmModal({
            title: "제출취소 실패",
            content: resultMsg || "제출취소에 실패했습니다.",
            okText: "확인",
            cancelButtonProps: { style: { display: "none" } },
            onOk: () => {
              common.openPage({ menuId: "MEVL2025002" });
            },
          });
        } else {
          message.success("제출취소 되었습니다.");
          setTimeout(() => {
            common.openPage({ menuId: "MEVL2025002" });
          }, 800);
        }
      },
      error: (err: any) => {
        openConfirmModal({
          title: "오류",
          content: "제출취소에 실패했습니다.",
          okText: "확인",
          cancelButtonProps: { style: { display: "none" } },
          onOk: () => {
            common.openPage({ menuId: "MEVL2025002" });
          },
        });
      },
    });
  };

  /**
   * 등급별 점수 계산 함수
   * @param grade - 평가 등급 (A, B, C, D)
   * @param score - 배점
   * @returns 계산된 점수 (배점 × 등급 계수)
   *
   * 등급 계수:
   * - A: 1.0 (100%)
   * - B: 0.75 (75%)
   * - C: 0.5 (50%)
   * - D: 0.25 (25%)
   */
  const calculateScore = (grade: string, score: number): number => {
    const gradeMap: Record<string, number> = {
      A: 1.0,
      B: 0.75,
      C: 0.5,
      D: 0.25,
    };
    return score * (gradeMap[grade] || 0);
  };

  /**
   * 구분별 소계 계산 함수
   * @param category - 평가 구분 (일정, 인력, 품질, 비용)
   * @returns 소계 점수 (소수점 첫째자리까지, 0이면 빈 문자열)
   *
   * 로직:
   * 1. 해당 구분의 평가 항목들만 필터링 (소계 행 제외)
   * 2. 각 항목의 등급을 확인 (사용자가 선택한 값 우선, 없으면 초기값)
   * 3. 등급이 있는 경우에만 점수 계산하여 합산
   * 4. 총합이 0보다 크면 소수점 첫째자리까지 반환, 아니면 빈 문자열
   */
  const calculateSubtotal = (category: string): string => {
    // 해당 구분의 평가 항목만 필터링 (no가 빈 문자열인 소계 행은 제외)
    const categoryItems = evaluationData.filter(
      (item) => item.category === category && item.no !== "",
    );

    // 각 항목의 점수를 계산하여 합산
    const total = categoryItems.reduce((sum, item) => {
      // pmScores 객체에 명시적으로 있는지 확인 (사용자가 선택한 경우)
      // 없으면 초기값(item.pm) 사용
      const grade = item.key in pmScores ? pmScores[item.key] : item.pm;
      if (grade && grade !== "") {
        return sum + calculateScore(grade, item.score);
      }
      return sum;
    }, 0);

    // 총합이 0보다 크면 소수점 첫째자리 문자열로 반환, 아니면 빈 문자열
    return total > 0 ? total.toFixed(2) : "";
  };

  /**
   * 총계 계산 함수
   * @returns 총계 점수 (소수점 첫째자리까지, 0이면 빈 문자열)
   *
   * 로직:
   * 1. 4개 구분(일정, 인력, 품질, 비용)의 소계를 모두 합산
   * 2. 기타가감 항목의 점수를 추가
   * 3. 최종 총계 반환
   */
  const calculateGrandTotal = (): string => {
    const categories = ["일정", "인력", "품질", "비용"];
    let total = 0;

    // 각 구분의 소계를 합산
    categories.forEach((category) => {
      const subtotal = calculateSubtotal(category);
      if (subtotal) {
        total += parseFloat(subtotal);
      }
    });

    // 기타가감 항목 추가 (dtlTypCd: '11')
    const otherItem = evaluationData.find((item) => item.dtlTypCd === "11");
    if (otherItem) {
      const grade =
        otherItem.key in pmScores ? pmScores[otherItem.key] : otherItem.pm;
      if (grade && grade !== "") {
        total += calculateScore(grade, otherItem.score);
      }
    }

    return total > 0 ? total.toFixed(2) : "";
  };

  /**
   * 평가 항목 테이블 컬럼 정의
   * - 구분: 평가 카테고리 (일정, 인력, 품질, 비용, 기타가감), rowSpan으로 구분별 병합 처리
   * - 평가항목: 각 평가 항목명, 번호 포함
   * - 배점: 각 항목의 배점
   * - PM: 평가 등급 선택 드롭다운 또는 계산된 점수 표시
   */
  const columns = [
    {
      title: "구분",
      dataIndex: "category",
      key: "category",
      align: "center" as const,
      width: 100,
      /**
       * 구분 컬럼 셀 병합 처리
       * dtlTypCd 별로 그룹화하여 병합
       */
      onCell: (record: EvaluationItem) => {
        if (record.dtlTypCd && record.key === firstKeys[record.dtlTypCd]) {
          return { rowSpan: groupSizes[record.dtlTypCd] };
        }
        if (record.dtlTypCd) {
          return { rowSpan: 0 };
        }
        // 계, 총계 행은 구분 컬럼 숨김 (colSpan: 0)
        if (record.item === "계" || record.item === "총계") {
          return { colSpan: 0 };
        }
        return {};
      },
    },
    {
      title: "평가항목",
      dataIndex: "item",
      key: "item",
      onCell: (record: EvaluationItem) => {
        // 계, 총계 행은 구분 컬럼까지 병합 (colSpan: 2)
        if (record.item === "계" || record.item === "총계") {
          return { colSpan: 2 };
        }
        return {};
      },
      render: (_: any, record: EvaluationItem) => {
        return record.item;
      },
    },
    {
      title: "배점",
      dataIndex: "score",
      key: "score",
      width: 100,
      align: "center" as const,
      render: (value: number) => value.toFixed(2),
    },
    {
      title: "PM",
      dataIndex: "pm",
      key: "pm",
      width: 100,
      align: "center" as const,
      render: (_: any, record: EvaluationItem) => {
        // 소계, 계, 총계는 계산값 표시 (콤보박스 없음)
        if (record.item === "소계") {
          const subtotal = isPreview ? "" : calculateSubtotal(record.category);
          return subtotal;
        }
        if (record.item === "계") {
          if (isPreview) return "";
          const categories = ["일정", "인력", "품질", "비용"];
          let total = 0;
          categories.forEach((category) => {
            const subtotal = calculateSubtotal(category);
            if (subtotal) {
              total += parseFloat(subtotal);
            }
          });
          return total > 0 ? total.toFixed(2) : "";
        }
        if (record.item === "총계") {
          const grandTotal = isPreview ? "" : calculateGrandTotal();
          return grandTotal;
        }

        // 일반 평가 항목은 콤보박스 표시
        return (
          <Select
            className="center-select"
            style={{ width: "100%" }}
            placeholder="선택"
            allowClear
            disabled={isReadOnlyMode || isPreview}
            options={[
              { value: "A", label: "A" },
              { value: "B", label: "B" },
              { value: "C", label: "C" },
              { value: "D", label: "D" },
            ]}
            value={isPreview ? undefined : (record.key in pmScores ? pmScores[record.key] : undefined)}
            onChange={(value) => {
              const newPmScores = { ...pmScores };
              if (value === undefined) {
                delete newPmScores[record.key];
              } else {
                newPmScores[record.key] = value;
              }
              dispatch(Evl0107mAction.setPmScores(newPmScores));
            }}
          />
        );
      },
    },
  ];

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          flexDirection: "column",
        }}
      >
        <Spin size="large">
          <div style={{ padding: 50 }} />
        </Spin>
        <div style={{ marginTop: 16, color: "#666" }}>
          데이터를 불러오는 중입니다...
        </div>
      </div>
    );
  }

  return (
    <div
      ref={scrollRef}
      style={{
        padding: "24px",
        maxWidth: "1200px",
        margin: "0 auto",
      }}
    >
      <Title level={2} style={{ textAlign: "center", marginBottom: "24px" }}>
        프로젝트 평가 (PM)
      </Title>

      <Form form={form} layout="vertical">
        {/* 프로젝트 정보 박스 */}
        <Card
          style={{ marginBottom: "24px" }}
          styles={{ body: { padding: "16px" } }}
        >
          <Table
            dataSource={[
              {
                key: "1",
                projectName: isPreview ? "" : projectInfo.pjNm,
                user: isPreview ? "" : projectInfo.userNm,
                department: isPreview ? "" : projectInfo.deptNm,
              },
            ]}
            columns={[
              {
                title: "프로젝트명",
                dataIndex: "label1",
                key: "label1",
                width: 120,
                align: "center" as const,
                render: () => "프로젝트명",
              },
              {
                dataIndex: "projectName",
                key: "projectName",
                width: 400,
                render: () => (
                  <Tooltip title={isPreview ? "" : projectInfo.pjNm}>
                    <Input
                      value={isPreview ? "" : projectInfo.pjNm}
                      onChange={(e) =>
                        dispatch(
                          Evl0107mAction.setProjectInfo({
                            ...projectInfo,
                            pjNm: e.target.value,
                          }),
                        )
                      }
                      placeholder=""
                      variant="borderless"
                      style={{ padding: "4px 11px", fontWeight: "bold" }}
                      readOnly
                    />
                  </Tooltip>
                ),
              },
              {
                title: "PM",
                dataIndex: "label2",
                key: "label2",
                width: 100,
                align: "center" as const,
                render: () => "PM",
              },
              {
                dataIndex: "pm",
                key: "pm",
                width: 150,
                render: () => (
                  <Tooltip title={isPreview ? "" : projectInfo.userNm}>
                    <Input
                      value={isPreview ? "" : projectInfo.userNm}
                      onChange={(e) =>
                        dispatch(
                          Evl0107mAction.setProjectInfo({
                            ...projectInfo,
                            userNm: e.target.value,
                          }),
                        )
                      }
                      placeholder=""
                      variant="borderless"
                      style={{
                        padding: "4px 11px",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                      readOnly
                    />
                  </Tooltip>
                ),
              },
              {
                title: "소속",
                dataIndex: "label3",
                key: "label3",
                width: 100,
                align: "center" as const,
                render: () => "소속",
              },
              {
                dataIndex: "department",
                key: "department",
                width: 150,
                render: () => (
                  <Tooltip title={isPreview ? "" : projectInfo.deptNm}>
                    <Input
                      value={isPreview ? "" : projectInfo.deptNm}
                      onChange={(e) =>
                        dispatch(
                          Evl0107mAction.setProjectInfo({
                            ...projectInfo,
                            deptNm: e.target.value,
                          }),
                        )
                      }
                      placeholder=""
                      variant="borderless"
                      style={{
                        padding: "4px 11px",
                        fontWeight: "bold",
                        textAlign: "center",
                      }}
                      readOnly
                    />
                  </Tooltip>
                ),
              },
            ]}
            pagination={false}
            bordered
            size="small"
            showHeader={false}
          />
        </Card>
        <Title level={4} style={{ marginBottom: "16px" }}>
          평가 항목
        </Title>

        <Card
          style={{ marginBottom: "24px", borderRadius: 8 }}
          bodyStyle={{ padding: 16 }}
        >
          <div
            style={{
              marginBottom: "12px",
              padding: "8px",
              backgroundColor: "#f5f5f5",
              borderRadius: 6,
            }}
          >
            <span>
              평가방법: A (우수), B (양호), C (보통), D (미흡)의 4단계로 평가
            </span>
          </div>

          <div ref={evaluationTableRef} data-eval-table="true">
            <Table
              dataSource={evaluationData}
              columns={columns}
              pagination={false}
              bordered
              size="small"
              rowClassName={(record) => {
                if (
                  record.item === "소계" ||
                  record.item === "계" ||
                  record.item === "총계"
                ) {
                  return "subtotal-row";
                }
                return "";
              }}
              onRow={(record) =>
                ({
                  "data-eval-key": record.key,
                }) as React.HTMLAttributes<HTMLElement>
              }
            />
          </div>
        </Card>

        {/* 점수 집계 테이블 - 각 구분별 점수를 한눈에 확인할 수 있는 요약 테이블 */}
        <div style={{ marginBottom: "24px" }}>
          <Table
            dataSource={[
              {
                key: "1",
                schedulePm: isPreview ? "" : calculateSubtotal("일정"),
                scheduleScore: "40.0",
                personnelPm: isPreview ? "" : calculateSubtotal("인력"),
                personnelScore: "20.0",
                qualityPm: isPreview ? "" : calculateSubtotal("품질"),
                qualityScore: "20.0",
                costPm: isPreview ? "" : calculateSubtotal("비용"),
                costScore: "20.0",
                otherPm: isPreview ? "" : (() => {
                  const otherItem = evaluationData.find(
                    (item) => item.dtlTypCd === "11",
                  );
                  if (otherItem) {
                    const grade =
                      otherItem.key in pmScores
                        ? pmScores[otherItem.key]
                        : otherItem.pm;
                    if (grade && grade !== "") {
                      return calculateScore(grade, otherItem.score).toFixed(2);
                    }
                  }
                  return "";
                })(),
                otherScore: "5.0",
                totalPm: isPreview ? "" : calculateGrandTotal(),
              },
            ]}
            columns={[
              {
                title: "일정관리",
                children: [
                  {
                    title: "PM",
                    dataIndex: "schedulePm",
                    key: "schedulePm",
                    align: "center" as const,
                    width: 60,
                  },
                  {
                    title: "배점",
                    dataIndex: "scheduleScore",
                    key: "scheduleScore",
                    align: "center" as const,
                    width: 60,
                  },
                ],
              },
              {
                title: "인력관리",
                children: [
                  {
                    title: "PM",
                    dataIndex: "personnelPm",
                    key: "personnelPm",
                    align: "center" as const,
                    width: 60,
                  },
                  {
                    title: "배점",
                    dataIndex: "personnelScore",
                    key: "personnelScore",
                    align: "center" as const,
                    width: 60,
                  },
                ],
              },
              {
                title: "품질관리",
                children: [
                  {
                    title: "PM",
                    dataIndex: "qualityPm",
                    key: "qualityPm",
                    align: "center" as const,
                    width: 60,
                  },
                  {
                    title: "배점",
                    dataIndex: "qualityScore",
                    key: "qualityScore",
                    align: "center" as const,
                    width: 60,
                  },
                ],
              },
              {
                title: "비용관리",
                children: [
                  {
                    title: "PM",
                    dataIndex: "costPm",
                    key: "costPm",
                    align: "center" as const,
                    width: 60,
                  },
                  {
                    title: "배점",
                    dataIndex: "costScore",
                    key: "costScore",
                    align: "center" as const,
                    width: 60,
                  },
                ],
              },
              {
                title: "기타가감",
                children: [
                  {
                    title: "PM",
                    dataIndex: "otherPm",
                    key: "otherPm",
                    align: "center" as const,
                    width: 60,
                  },
                  {
                    title: "배점",
                    dataIndex: "otherScore",
                    key: "otherScore",
                    align: "center" as const,
                    width: 60,
                  },
                ],
              },
              {
                title: "총계",
                dataIndex: "totalPm",
                key: "totalPm",
                align: "center" as const,
                width: 80,
              },
            ]}
            pagination={false}
            bordered
            size="small"
          />
        </div>

        <style>{`
          .subtotal-row {
            background-color: #fafafa;
            font-weight: 600;
            text-align: center;
          }
          .center-select .ant-select-selector {
            text-align: center !important;
          }
          .center-select .ant-select-selection-item {
            text-align: center !important;
            justify-content: center !important;
            display: flex !important;
          }
          .center-select .ant-select-selection-placeholder {
            text-align: center !important;
            justify-content: center !important;
            display: flex !important;
          }
          .ant-select-item-option-content {
            text-align: center !important;
            justify-content: center !important;
            display: flex !important;
          }
          .ant-table-thead th {
            text-align: center !important;
          }
          .error-select .ant-select-selector {
            border-color: red !important;
          }
        `}</style>

        {/* 기타 평가 의견 */}
        <Title level={4} style={{ marginBottom: "16px" }}>
          기타 평가 의견
        </Title>

        <Form.Item name="comments">
          {/* <div ref={commentsRef}> */}
          <TextArea
            rows={8}
            placeholder=""
            disabled={isReadOnlyMode}
            onChange={handleCommentsChange}
            maxLength={500}
            showCount
            value={isPreview ? "" : undefined}
          />
          {/* </div> */}
        </Form.Item>

        {/* 버튼 영역 - 미리보기/읽기전용 모드에서는 전체 숨김 */}
        {!isPreview && !isReadonly && (
          <>
            {pmSubYn !== "Y" && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <Button onClick={handleSave}>저장</Button>
                <Button type="primary" onClick={handleSubmit}>
                  제출
                </Button>
              </div>
            )}
            {pmSubYn === "Y" && isWithinDeadline && (
              <div
                style={{
                  display: "flex",
                  gap: "12px",
                  justifyContent: "flex-end",
                }}
              >
                <Button danger onClick={handleCancelSubmit}>
                  제출취소
                </Button>
              </div>
            )}
          </>
        )}
      </Form>
    </div>
  );
}
